import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import Globe from 'react-globe.gl';
import { ArrowLeft, MapPin, Activity, Users, Clock, RefreshCw, Eye, TrendingUp } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { toast } from 'sonner';

const IndiaVisitorMapEnhanced = () => {
  const navigate = useNavigate();
  const globeEl = useRef();
  const [visitors, setVisitors] = useState([]);
  const [detailedVisitors, setDetailedVisitors] = useState([]);
  const [stats, setStats] = useState({ total: 0, cities: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
      return;
    }

    loadVisitorLocations();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadVisitorLocations();
    }, 30000);

    return () => clearInterval(interval);
  }, [navigate]);

  // Set globe camera to focus on India with better view
  useEffect(() => {
    if (globeEl.current && !isLoading) {
      globeEl.current.pointOfView({ lat: 23, lng: 78, altitude: 2.2 }, 1000);
      
      // Enable globe controls
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 0.5;
    }
  }, [isLoading]);

  const loadVisitorLocations = async () => {
    try {
      const [liveData, todayData] = await Promise.all([
        adminAPI.getVisitorDetails('live'),
        adminAPI.getVisitorDetails('today')
      ]);

      const allVisitors = [...liveData.visitors, ...todayData.visitors];
      const visitorsWithLocation = allVisitors.filter(v => 
        v.location && v.location.latitude && v.location.longitude
      );

      const cities = [...new Set(visitorsWithLocation.map(v => `${v.location.city}, ${v.location.region}`))];

      // Store detailed visitor info
      setDetailedVisitors(visitorsWithLocation);

      // Transform for 3D globe with enhanced visual
      const pointsData = visitorsWithLocation.map((v, index) => ({
        lat: v.location.latitude,
        lng: v.location.longitude,
        size: 0.4,
        color: index % 2 === 0 ? '#DC2626' : '#F59E0B', // Alternating red and orange
        city: v.location.city,
        region: v.location.region,
        country: v.location.country,
        sessionId: v.sessionId || `Session ${index + 1}`,
        timestamp: v.timestamp,
        page: v.page || '/'
      }));

      setVisitors(pointsData);
      setStats({
        total: visitorsWithLocation.length,
        cities: cities
      });
      setLastRefresh(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading visitor locations:', error);
      toast.error('Failed to load visitor map');
      setIsLoading(false);
    }
  };

  const handlePointClick = (point) => {
    setHoveredPoint(point);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading visitor map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ 
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)'
    }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 text-white drop-shadow-lg flex items-center gap-3">
              <Eye className="h-10 w-10 text-yellow-400 animate-pulse" />
              Live Visitor Map - 3D Globe
            </h1>
            <p className="text-blue-100 text-lg font-medium">Track visitors in real-time across India</p>
          </div>
          <Button 
            onClick={() => navigate('/admin/dashboard')}
            className="bg-white text-blue-900 hover:bg-blue-50 font-semibold border-2 border-blue-300"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="border-4 border-yellow-400 shadow-2xl bg-gradient-to-br from-yellow-50 to-orange-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Visitors</p>
                  <p className="text-4xl font-bold text-orange-600">{stats.total}</p>
                </div>
                <Users className="h-12 w-12 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-4 border-green-400 shadow-2xl bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Unique Cities</p>
                  <p className="text-4xl font-bold text-green-600">{stats.cities.length}</p>
                </div>
                <MapPin className="h-12 w-12 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-4 border-purple-400 shadow-2xl bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                  <p className="text-lg font-bold text-purple-600">
                    {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <RefreshCw className="h-12 w-12 text-purple-400 animate-spin" style={{ animationDuration: '3s' }} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* 3D Globe - Smaller Size */}
          <div className="lg:col-span-2">
            <Card className="border-4 border-blue-400 shadow-2xl bg-white/95 backdrop-blur-sm overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-6 w-6 animate-pulse" />
                  Interactive 3D Visitor Globe
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="bg-gradient-to-b from-blue-950 to-black rounded-xl overflow-hidden border-4 border-blue-300" 
                     style={{ height: '500px' }}>
                  <Globe
                    ref={globeEl}
                    globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
                    backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
                    pointsData={visitors}
                    pointAltitude={0.02}
                    pointColor="color"
                    pointRadius={0.8}
                    pointLabel={d => `
                      <div style="background: rgba(0,0,0,0.85); color: white; padding: 12px; border-radius: 8px; border: 2px solid ${d.color}; min-width: 200px;">
                        <div style="font-size: 16px; font-weight: bold; color: ${d.color}; margin-bottom: 8px;">📍 Visitor Location</div>
                        <div style="font-size: 14px; margin-bottom: 4px;"><strong>City:</strong> ${d.city}</div>
                        <div style="font-size: 14px; margin-bottom: 4px;"><strong>State:</strong> ${d.region}</div>
                        <div style="font-size: 14px; margin-bottom: 4px;"><strong>Country:</strong> ${d.country}</div>
                        <div style="font-size: 12px; color: #9ca3af; margin-top: 8px;">Click for more details</div>
                      </div>
                    `}
                    onPointClick={handlePointClick}
                    atmosphereColor="#3b82f6"
                    atmosphereAltitude={0.3}
                    width={700}
                    height={480}
                  />
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-600 rounded-full animate-pulse"></div>
                      <span className="font-medium">Active</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                      <span className="font-medium">Recent</span>
                    </div>
                  </div>
                  <span className="text-blue-600 font-bold">🌐 Drag to rotate • Scroll to zoom</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visitor Details Panel */}
          <div>
            <Card className="border-4 border-pink-400 shadow-2xl bg-gradient-to-br from-pink-50 to-rose-50 h-full">
              <CardHeader className="bg-gradient-to-r from-pink-600 to-rose-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6" />
                  Visitor Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {hoveredPoint ? (
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border-2 border-pink-300 shadow-md">
                      <h3 className="font-bold text-lg mb-3 text-pink-600">Selected Visitor</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">📍 City:</span>
                          <span className="font-bold text-gray-900">{hoveredPoint.city}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">🗺️ State:</span>
                          <span className="font-bold text-gray-900">{hoveredPoint.region}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">🌍 Country:</span>
                          <span className="font-bold text-gray-900">{hoveredPoint.country}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">📄 Page:</span>
                          <span className="font-bold text-gray-900">{hoveredPoint.page}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MapPin className="h-16 w-16 text-pink-300 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Click on any point on the globe to see visitor details</p>
                  </div>
                )}

                {/* Recent Visitors List */}
                <div className="mt-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-800">Recent Visitors</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {detailedVisitors.slice(0, 10).map((visitor, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border-2 border-gray-200 hover:border-pink-400 transition-all">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-sm text-gray-900">
                              {visitor.location.city}, {visitor.location.region}
                            </p>
                            <p className="text-xs text-gray-500">{visitor.page || '/'}</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <Activity className="h-3 w-3 mr-1" />
                            Live
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndiaVisitorMapEnhanced;
